import React, { useId, useState } from "react";

// Brand-derived palettes: [gradient start, gradient end, glow]. Forest green and
// terracotta come from --primary / --accent; the rest stay in the same warm,
// regional register (laterite, savanna, indigo dye) so every cover still reads
// as Startup Café.
const PALETTES = [
  ["#16392c", "#2a634b", "#e0763a"], // forest → terracotta
  ["#123a34", "#1d5a4c", "#d9a441"], // deep teal → gold
  ["#3a261a", "#8a4b27", "#f09a55"], // laterite earth
  ["#172a40", "#27496a", "#e3b35a"], // Sahel night → sand
  ["#27341b", "#4c6a2e", "#e6c16a"], // savanna
  ["#3b2032", "#6b3350", "#f0a060"], // hibiscus
  ["#1d3b2a", "#3f7a57", "#f2c14e"], // baobab
  ["#231f3f", "#443a74", "#e98a5b"], // indigo dye
];

// Small stable string hash (FNV-1a), so an item keeps the same cover forever.
function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function Pattern({ id, kind }) {
  if (kind === 0) {
    return (
      <pattern id={id} width="18" height="18" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="1.4" fill="#fff" fillOpacity="0.14" />
      </pattern>
    );
  }
  if (kind === 1) {
    return (
      <pattern id={id} width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
        <line x1="0" y1="0" x2="0" y2="14" stroke="#fff" strokeOpacity="0.08" strokeWidth="2" />
      </pattern>
    );
  }
  // A zigzag band, a nod to the geometric weaves of the region.
  return (
    <pattern id={id} width="24" height="16" patternUnits="userSpaceOnUse">
      <path d="M0 12 L6 4 L12 12 L18 4 L24 12" fill="none" stroke="#fff" strokeOpacity="0.09" strokeWidth="1.5" />
    </pattern>
  );
}

/**
 * Cover image for any public listing (startup, programme, event): the given
 * image when there is one and it loads, otherwise a generated cover in the
 * brand palette. Pure SVG — nothing to download, which matters on the slow
 * connections most visitors have. Fills its container; the container sets the
 * aspect ratio.
 *
 * - `src`: an already-resolved image URL (pass it through mediaSrc() where the
 *   page does), or empty.
 * - `seed`: a stable id or slug, so the same item always gets the same cover.
 * - `label`: the name/title whose initial is drawn in the background.
 */
export default function Cover({ src, seed, label, alt = "", imgClassName = "h-full w-full object-cover", testid = "cover-generated" }) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  // A URL that 404s must not leave the bare container behind.
  const [failedSrc, setFailedSrc] = useState(null);
  if (src && failedSrc !== src) {
    return <img src={src} alt={alt} className={imgClassName} loading="lazy" onError={() => setFailedSrc(src)} />;
  }

  // Palette, pattern, angle and glow side each take different bits of the hash,
  // so two neighbouring cards rarely come out looking identical.
  const h = hash(String(seed || label || "startupcafe"));
  const [from, to, glow] = PALETTES[h % PALETTES.length];
  const kind = (h >>> 8) % 3;
  const angle = 110 + ((h >>> 12) % 60);
  const glowLeft = ((h >>> 20) & 1) === 1;
  const initial = (String(label || "").trim().charAt(0) || "S").toUpperCase();

  return (
    <div className="relative h-full w-full overflow-hidden" aria-hidden="true" data-testid={testid}
         style={{ background: `linear-gradient(${angle}deg, ${from} 0%, ${to} 100%)` }}>
      {/* viewBox + "slice" keeps the design centred from 16:9 cards to 3:1 banners. */}
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 400 225" preserveAspectRatio="xMidYMid slice">
        <defs>
          <Pattern id={`${uid}-p`} kind={kind} />
          <radialGradient id={`${uid}-g`} cx={glowLeft ? "18%" : "82%"} cy="18%" r="65%">
            <stop offset="0" stopColor={glow} stopOpacity="0.5" />
            <stop offset="1" stopColor={glow} stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`${uid}-s`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0.55" stopColor="#000" stopOpacity="0" />
            <stop offset="1" stopColor="#000" stopOpacity="0.25" />
          </linearGradient>
        </defs>
        <rect width="400" height="225" fill={`url(#${uid}-p)`} />
        <rect width="400" height="225" fill={`url(#${uid}-g)`} />
        {/* Kept inside the band visible at 3:1, so the initial is never cropped. */}
        <text x="372" y="166" textAnchor="end" className="font-display" fontSize="132" fontWeight="700"
              fill="#fff" fillOpacity="0.12">{initial}</text>
        <rect width="400" height="225" fill={`url(#${uid}-s)`} />
      </svg>
    </div>
  );
}
